import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDownloadPdf } from '@/hooks/useDownloadPdf';
import PdfCoverDialog from '@/components/PdfCoverDialog';
import {
  ArrowLeft, Edit, Trash2, FileText, PenTool, Sparkles, Printer, Download, Loader2,
  Clipboard, Table, Archive, Presentation, Brain, Bookmark,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

/**
 * UnitActionBar — quick-action rail for a curriculum unit.
 *
 * Mobile behavior:
 *  - "Back to Dashboard" pins to the top on its own row.
 *  - Action buttons live in a horizontally scrollable rail with snap-to-item
 *    scroll so a phone user can swipe between them without wrapping.
 *  - Icon-only "delete" stays as the terminal chip on the right.
 */
export default function UnitActionBar({
  unit,
  unitId,
  pinned,
  pinLoading,
  showAIGenerator,
  onTogglePin,
  onToggleAIGenerator,
  onPrint,
  onDelete,
}) {
  const navigate = useNavigate();
  const { downloading: downloadingPdf, downloadPdf } = useDownloadPdf();
  const [coverOpen, setCoverOpen] = useState(false);

  const handleDownloadPdf = () => setCoverOpen(true);
  const handleCoverConfirm = async (cover) => {
    const filename = `unit-${(unit?.title || 'unit').replace(/[^a-z0-9-]+/gi, '-').toLowerCase().slice(0, 40)}.pdf`;
    await downloadPdf({
      resource: 'unit',
      resourceId: unitId,
      filename,
      cover: {
        ...cover,
        title: unit?.title,
        grade: unit?.grade_level ? String(unit.grade_level) : '',
        unit_number: unit?.unit_number != null ? String(unit.unit_number) : '',
        resource_label: 'Curriculum Unit',
      },
    });
    setCoverOpen(false);
  };

  const chip = 'flex-shrink-0 snap-start';

  return (
    <div className="mb-6 no-print" data-testid="unit-action-bar">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(`/dashboard/${unit.grade_level}`)}
        className="text-[#5A6577] hover:text-[#14213D] mb-3 -ml-2"
        data-testid="back-to-dashboard-btn"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Dashboard
      </Button>
      <div
        className="flex items-center gap-2 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap sm:overflow-visible sm:justify-end sm:pb-0"
        data-testid="unit-action-rail"
      >
        <Button
          variant={pinned ? 'default' : 'outline'}
          size="sm"
          onClick={onTogglePin}
          disabled={pinLoading}
          className={`${chip} ${pinned
            ? 'bg-[#D4AF37] hover:bg-[#C19E2C] text-[#14213D] border-0'
            : 'border-[#D4AF37]/40 text-[#14213D] hover:bg-[#FBF6E8]'}`}
          data-testid="pin-unit-btn"
          title={pinned ? 'Unpin from your collection' : 'Pin to your collection'}
        >
          <Bookmark className={`h-4 w-4 mr-2 ${pinned ? 'fill-current' : ''}`} />
          {pinned ? 'Pinned' : 'Pin'}
        </Button>
        <Button variant="outline" size="sm" onClick={() => navigate(`/rubric-generator/${unitId}`)} className={chip} data-testid="rubric-generator-btn">
          <Table className="h-4 w-4 mr-2" /> Rubric
        </Button>
        <Button variant="outline" size="sm" onClick={() => navigate(`/skeleton-notes/${unitId}`)} className={chip} data-testid="skeleton-notes-btn">
          <Clipboard className="h-4 w-4 mr-2" /> Notes
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/lesson-builder', {
            state: { unit_title: unit.title, topic: unit.description, grade_level: unit.grade_level }
          })}
          className={chip}
          data-testid="lesson-builder-btn"
        >
          <FileText className="h-4 w-4 mr-2" /> Lesson Plan
        </Button>
        <Button variant="outline" size="sm" onClick={() => navigate(`/primary-sources/${unitId}`)} className={chip} data-testid="primary-sources-btn">
          <Archive className="h-4 w-4 mr-2" /> Sources
        </Button>
        <Button variant="outline" size="sm" onClick={() => navigate(`/lecture-notes/${unitId}`)} className={chip} data-testid="lecture-notes-btn">
          <Presentation className="h-4 w-4 mr-2" /> Lecture
        </Button>
        <Button variant="outline" size="sm" onClick={() => navigate(`/quiz/${unitId}`)} className={chip} data-testid="quiz-btn">
          <Brain className="h-4 w-4 mr-2" /> Quiz
        </Button>
        <Button variant="outline" size="sm" onClick={() => navigate(`/essay-grader/${unitId}`)} className={chip} data-testid="essay-grader-btn">
          <PenTool className="h-4 w-4 mr-2" /> Essays
        </Button>
        <Button variant="outline" size="sm" onClick={onPrint} className={chip} data-testid="print-btn">
          <Printer className="h-4 w-4 mr-2" /> Print
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleDownloadPdf}
          disabled={downloadingPdf}
          className={chip}
          data-testid="download-pdf-btn"
          title="Download this unit as a PDF"
        >
          {downloadingPdf ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> PDF&hellip;</>
          ) : (
            <><Download className="h-4 w-4 mr-2" /> PDF</>
          )}
        </Button>
        <Button variant="outline" size="sm" onClick={onToggleAIGenerator} className={chip} data-testid="ai-generator-btn">
          <Sparkles className="h-4 w-4 mr-2" /> AI
        </Button>
        <Button variant="outline" size="sm" onClick={() => navigate(`/edit/${unitId}`)} className={chip} data-testid="edit-unit-btn">
          <Edit className="h-4 w-4 mr-2" /> Edit
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              data-testid="delete-unit-btn"
              className={`${chip} text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Unit</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this curriculum unit? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={onDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-btn"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      <PdfCoverDialog
        open={coverOpen}
        onOpenChange={setCoverOpen}
        onConfirm={handleCoverConfirm}
        downloading={downloadingPdf}
        context={{
          title: unit?.title,
          resource_label: 'Curriculum Unit',
          grade: unit?.grade_level,
          unit_number: unit?.unit_number,
        }}
      />
    </div>
  );
}
